import { FC, useCallback, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Toggle } from "components/inputs/Toggle"

import { settingsService } from "services/settings/settings.service"
import { CryptoCoinsSettings } from "services/settings/type"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface CryptoCoinsTabProps {
  cryptoCoins: CryptoCoinsSettings
  onChange: (cryptoCoins: CryptoCoinsSettings) => void
  refreshSettings: () => Promise<void>
}

export const CryptoCoinsTab: FC<CryptoCoinsTabProps> = ({ cryptoCoins, onChange, refreshSettings }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [isSaving, setIsSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  // Sort coins alphabetically for consistent display order
  const sortedCoins = Object.keys(cryptoCoins).sort((a, b) => a.localeCompare(b))

  const handleToggle = useCallback(
    (coin: string, field: "depositEnabled" | "withdrawEnabled") => {
      onChange({
        ...cryptoCoins,
        [coin]: {
          ...cryptoCoins[coin],
          [field]: !cryptoCoins[coin][field]
        }
      })
    },
    [cryptoCoins, onChange]
  )

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const success = await settingsService.updateCryptoCoins(cryptoCoins)
      if (success) {
        toast.success(t("settings.cryptoCoins.updated") as string)
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.tabs.cryptoCoins")}</h3>
        <SettingsHistoryNote target="crypto" refreshKey={historyKey} />
      </div>

      <div className="rounded-lg border border-black/20 dark:border-white/10 bg-white dark:bg-transparent overflow-hidden">
        <div className="grid grid-cols-3 bg-black/[0.03] dark:bg-white/[0.04] border-b border-black/10 dark:border-white/10 px-4 py-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t("settings.cryptoCoins.coin")}</span>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-center">
            {t("settings.cryptoCoins.deposit")}
          </span>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-center">
            {t("settings.cryptoCoins.withdraw")}
          </span>
        </div>

        {sortedCoins.map((coin, index) => {
          const setting = cryptoCoins[coin]
          return (
            <div
              key={coin}
              className={`grid grid-cols-3 items-center px-4 py-3 border-b border-black/10 dark:border-white/10 last:border-b-0 ${
                index % 2 === 1 ? "bg-black/[0.03] dark:bg-white/[0.04]" : ""
              }`}
            >
              <span className="text-base font-semibold text-gray-800 dark:text-white">{coin}</span>
              <div className="flex justify-center">
                <Toggle checked={setting.depositEnabled} onChange={() => handleToggle(coin, "depositEnabled")} disabled={isSaving} />
              </div>
              <div className="flex justify-center">
                <Toggle checked={setting.withdrawEnabled} onChange={() => handleToggle(coin, "withdrawEnabled")} disabled={isSaving} />
              </div>
            </div>
          )
        })}
      </div>

      <SettingsSectionActions
        onSave={handleSave}
        saving={isSaving}
        resetTarget="crypto"
        resetLabel={t("settings.tabs.cryptoCoins")}
        onResetDone={handleResetDone}
      />
    </section>
  )
}
